import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Flag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { ReviewCard } from "@/components/ReviewsBlock";
import ClientPhotosGallery from "@/components/ClientPhotosGallery";

const Stars = ({ n }) => (<span className="inline-flex text-terracotta">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={18} fill={i <= Math.round(n) ? "currentColor" : "none"} strokeWidth={1.5} />)}</span>);

export default function Reviews() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const [brand, setBrand] = useState({});

  useEffect(() => { api.get(`/reviews?sort=${sort}&page=${page}`).then((r) => setData(r.data)).catch(() => {}); }, [sort, page]);
  useEffect(() => { api.get("/brand").then((r) => setBrand(r.data)).catch(() => {}); }, []);

  const report = async (id) => {
    const note = window.prompt("Why are you reporting this review?");
    if (note === null) return;
    try { await api.post("/reviews/report", { review_id: id, note }); toast.success("Thank you — we'll look into it."); }
    catch { toast.error("Could not submit report"); }
  };

  const s = data?.summary || { count: 0, average: 0, breakdown: {} };
  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-12 py-16" data-testid="reviews-page">
      <h1 className="text-3xl sm:text-4xl font-normal text-charcoal-800 mb-6">{lang === "es" ? "Reseñas" : "Reviews"}</h1>

      {s.count === 0 ? (
        <p className="text-charcoal-500" data-testid="reviews-empty">{lang === "es" ? "Pronto habrá nuevas reseñas." : "New reviews coming soon."}</p>
      ) : (
        <div className="rounded-3xl border border-sand-200 bg-white p-6 mb-8 flex flex-wrap items-center gap-6" data-testid="reviews-summary">
          <div className="text-center"><div className="text-4xl font-semibold text-charcoal-800">{s.average}</div><Stars n={s.average} /><div className="text-xs text-charcoal-500 mt-1">{s.count} verified</div></div>
          <div className="flex-1 min-w-[200px] space-y-1">
            {[5, 4, 3, 2, 1].map((n) => {
              const c = s.breakdown[n] || 0; const pct = s.count ? (c / s.count) * 100 : 0;
              return (
                <button key={n} onClick={() => setSort(n >= 3 ? "highest" : "lowest")} className="w-full flex items-center gap-2 text-xs" data-testid={`bar-${n}`}>
                  <span className="w-6">{n}★</span>
                  <span className="flex-1 h-2 bg-sand-100 rounded-full overflow-hidden"><span className="block h-full bg-terracotta" style={{ width: `${pct}%` }} /></span>
                  <span className="w-6 text-right text-charcoal-500">{c}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {s.count > 0 && (
        <>
          <div className="flex items-center gap-2 mb-4 text-sm">
            <span className="text-charcoal-500">Sort:</span>
            {["recent", "highest", "lowest"].map((o) => (
              <button key={o} data-testid={`sort-${o}`} onClick={() => { setSort(o); setPage(1); }} className={`rounded-full px-3 py-1 ${sort === o ? "bg-terracotta text-white" : "bg-sand-100 text-charcoal-600"}`}>{o}</button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {data.items.map((r) => (
              <div key={r.id} className="relative">
                <ReviewCard r={r} />
                <button onClick={() => report(r.id)} data-testid={`report-${r.id}`} className="absolute top-4 right-4 text-charcoal-300 hover:text-red-500" title="Report"><Flag size={15} /></button>
              </div>
            ))}
          </div>
          {data.total > data.per_page && (
            <div className="flex justify-center gap-2 mt-6 text-sm">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full border px-4 py-1 disabled:opacity-40">Prev</button>
              <span className="px-3 py-1">Page {page}</span>
              <button disabled={page * data.per_page >= data.total} onClick={() => setPage(page + 1)} className="rounded-full border px-4 py-1 disabled:opacity-40">Next</button>
            </div>
          )}
        </>
      )}

      <ClientPhotosGallery />

      <div className="mt-10 flex flex-wrap gap-3 border-t border-sand-200 pt-6 text-sm">
        {brand.google_profile_url && <a href={brand.google_profile_url} target="_blank" rel="noreferrer" className="rounded-full border px-4 py-2">Find us on Google</a>}
        {brand.yelp_page_url && <a href={brand.yelp_page_url} target="_blank" rel="noreferrer" className="rounded-full border px-4 py-2">Find us on Yelp</a>}
        <Link to="/references" data-testid="reviews-to-references" className="rounded-full border px-4 py-2">{lang === "es" ? "Referencias personales" : "Personal references"}</Link>
        <Link to="/review-policy" className="ml-auto text-charcoal-500 underline self-center">How we handle reviews</Link>
      </div>
    </div>
  );
}
